import { useCallback, useEffect, useRef, useState } from 'react';

const posKey = (code) => `lc:deck:${String(code).toUpperCase()}`;

/** quiz stop index for a given question order_index, or -1 */
function stopIndexForOrder(stops, order) {
  return stops.findIndex((s) => s.kind === 'quiz' && s.order === order);
}

/**
 * One deck, two screens: the projected deck and the instructor's control
 * panel both run this hook, and the server holds the position they share
 * (see server/src/game/deckPosition.js). Whichever screen the arrow press
 * landed on works out where that press leads and publishes the *absolute*
 * result; every screen, the sender included, then renders what comes back.
 *
 * Two rules keep the two screens from fighting, both of them about having a
 * single owner rather than a clever merge:
 *
 *   - The server's position always wins. localStorage is consulted only when
 *     the server says it has none, which happens when it has restarted
 *     mid-lesson, and only the projected screen (`isProjector`) may offer it.
 *     A control panel opened fresh could be holding last week's slide.
 *   - Movement the instructor did not ask for — jumping to the results slide
 *     when the lesson ends — is likewise the projector's to perform.
 *
 * @param {object}  room         the useRoomChannel value
 * @param {Array}   stops        the built deck's stops
 * @param {boolean} isProjector  true on the screen the class is looking at
 */
export function useDeckPosition(room, stops, { isProjector = false } = {}) {
  const lastCount = stops.length;

  // buildDeck falls back to a generic three-slide deck until the room's
  // questions have arrived, and for the first moment after a page load that
  // is the deck this hook is holding. Anything that writes a position — the
  // localStorage fallback, the restore, the jump to the results slide — has
  // to wait for the real one, or it clamps slide 12 down to slide 2 and
  // saves that. Reading the server's position does not wait: it is clamped
  // to whatever is loaded and re-applied when the deck grows.
  const deckLoaded = room.questions.length > 0;

  const [index, setIndexState] = useState(0);
  const [substep, setSubstepState] = useState(0);
  const [direction, setDirection] = useState('forward');

  // Two arrow presses can land before React re-renders (key repeat, a fast
  // clicker), and a handler reading `index` out of its closure would compute
  // both from the same stale slide and swallow one. Refs update synchronously.
  const indexRef = useRef(0);
  const substepRef = useRef(0);
  const restoredRef = useRef(false);

  const stepsOf = useCallback((i) => Number(stops[i]?.steps) || 0, [stops]);

  const clamp = useCallback(
    (i) => Math.max(0, Math.min(i, Math.max(lastCount - 1, 0))),
    [lastCount]
  );

  /** Render an absolute position. Sends nothing — this is the receiving end. */
  const apply = useCallback(
    (i, s) => {
      const target = clamp(i);
      const arriveAt = Math.max(0, Math.min(s, stepsOf(target)));
      if (target !== indexRef.current) {
        setDirection(target > indexRef.current ? 'forward' : 'backward');
      }
      indexRef.current = target;
      substepRef.current = arriveAt;
      setIndexState(target);
      setSubstepState(arriveAt);
    },
    [clamp, stepsOf]
  );

  /** Move, and tell the server (and so the other screen) where we went. */
  const commit = useCallback(
    (i, s = 0) => {
      const from = indexRef.current;
      const target = clamp(i);
      const arriveAt = Math.max(0, Math.min(s, stepsOf(target)));
      if (target === from && arriveAt === substepRef.current) return;
      apply(target, arriveAt);
      room.actions.gotoSlide(target, arriveAt).catch(() => {});
      // Every slide change resets the classroom pulse for the new slide.
      // Walking a slide's reveal steps is not a new slide.
      if (target !== from) room.actions.newSlide();
    },
    [apply, clamp, stepsOf, room]
  );

  /** One press of a presenter remote: a reveal step if there is one, else a slide. */
  const goRel = useCallback(
    (delta) => {
      const cur = indexRef.current;
      const sub = substepRef.current;
      if (delta > 0 && sub < stepsOf(cur)) return commit(cur, sub + 1);
      if (delta < 0 && sub > 0) return commit(cur, sub - 1);
      // Arrowing backwards into a stepped slide lands on its finished state
      // rather than replaying it.
      return commit(cur + delta, delta < 0 ? stepsOf(cur + delta) : 0);
    },
    [commit, stepsOf]
  );

  // Follow the server.
  useEffect(() => {
    if (!room.position || lastCount === 0) return;
    apply(room.position.index, room.position.substep);
    // A position from the server means it is holding one again, so the next
    // time it says it has none — a second restart in the same lesson — the
    // projector is free to offer its own once more.
    restoredRef.current = false;
  }, [room.position, lastCount, apply]);

  // The server has been asked and holds nothing: it restarted. The projected
  // screen hands back what it remembers, nudged forward if a later question
  // is already live (the instructor refreshed mid-class).
  useEffect(() => {
    if (!isProjector || restoredRef.current || !deckLoaded) return;
    if (room.position !== null || lastCount === 0) return;
    restoredRef.current = true;
    let start = 0;
    try {
      const saved = Number(localStorage.getItem(posKey(room.roomCode)));
      if (Number.isFinite(saved) && saved > 0 && saved < lastCount) start = saved;
    } catch {
      /* private window, blocked storage — start from the top */
    }
    if (room.question && typeof room.question.orderIndex === 'number') {
      const qi = stopIndexForOrder(stops, room.question.orderIndex);
      if (qi > start) start = qi;
    }
    apply(start, 0);
    room.actions.gotoSlide(start, 0).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjector, room.position, lastCount, deckLoaded]);

  // Keep the fallback fresh, wherever the move came from.
  useEffect(() => {
    if (!isProjector || !deckLoaded) return;
    try {
      localStorage.setItem(posKey(room.roomCode), String(index));
    } catch {
      /* no-op */
    }
  }, [isProjector, deckLoaded, room.roomCode, index]);

  // Auto-follow the server if it ends the lesson (last question revealed).
  useEffect(() => {
    if (!isProjector || !deckLoaded || room.status !== 'ended') return;
    const ri = stops.findIndex((s) => s.kind === 'results');
    if (ri >= 0 && ri > indexRef.current) commit(ri, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjector, deckLoaded, room.status, lastCount]);

  return {
    index,
    substep,
    direction,
    lastCount,
    stop: stops[index] || stops[0] || null,
    nextStop: stops[index + 1] || null,
    stepsOf,
    goRel,
    goTo: useCallback((i) => commit(i, 0), [commit]),
  };
}
